import {announce} from '@github-ui/aria-live'
import {DragAndDrop, MoveDialogTrigger} from '@github-ui/drag-and-drop'
import {testIdProps} from '@github-ui/test-id-props'
import {CheckIcon, GrabberIcon, KebabHorizontalIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Heading, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'
import {CSSTransition} from 'react-transition-group'

import {SanitizedHtml} from '../../../../client/components/dom/sanitized-html'
import {apiUpdateColumn} from '../../../api/columns/api-update-column'
import type {MemexColumn} from '../../../api/columns/contracts/memex-column'
import {SettingsFieldReorder, SettingsFieldReorderSettingsUI} from '../../../api/stats/contracts'
import {getColumnIcon} from '../../../components/column-detail-helpers'
import useToasts from '../../../components/toasts/use-toasts'
import {isColumnUserEditable} from '../../../helpers/is-column-editable'
import {sortColumnsDeterministically} from '../../../helpers/util'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import type {ColumnModel} from '../../../models/column-model'
import {useProjectRouteParams} from '../../../router/use-project-route-params'
import {PROJECT_SETTINGS_FIELD_ROUTE} from '../../../routes'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {useIssueFieldsEnabled} from '../../../state-providers/issue-fields/use-issue-fields-enabled'
import generalSettingsStyles from './general-settings-view.module.css'
import styles from './reorder-custom-fields-form.module.css'

export type ReorderColumnCommitState = 'idle' | 'saving' | 'success' | 'error'

const getEditableColumns = (cols: Array<ColumnModel>) =>
  cols.filter(column => isColumnUserEditable(column)).sort(sortColumnsDeterministically)

const reorderFeedbackTimeout = 1000 //ms

const BoxFeedbackCommitState = ({className, ...props}: React.ComponentProps<'div'>) => (
  <div
    style={{'--reorder-feedback-timeout': `${reorderFeedbackTimeout}ms`}}
    className={clsx(styles.BoxFeedbackCommitState, className)}
    {...props}
  />
)

export function ReorderCustomFieldsForm() {
  const {postStats} = usePostStats()
  const routeParams = useProjectRouteParams()
  const {allColumns, setAllColumns} = useAllColumns()
  const issueFieldsEnabled = useIssueFieldsEnabled()

  const [sortableColumns, setSortableColumns] = useState(() => getEditableColumns(allColumns))
  const [reorderColumnCommitState, setReorderColumnCommitState] = useState<ReorderColumnCommitState>('idle')
  const {addToast} = useToasts()
  const saveFeedbackRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSortableColumns(getEditableColumns(allColumns))
  }, [allColumns])

  const onDrop = async ({
    dragMetadata,
    dropMetadata,
    isBefore,
  }: {
    dragMetadata: {id: number}
    dropMetadata?: {id: number}
    isBefore?: boolean
  }) => {
    if (dragMetadata.id === dropMetadata?.id) {
      return
    }

    let dropColumnId: number
    // dropMetadata is only null when the drop happens after all items of the list
    if (!dropMetadata) {
      const lastColumn = sortableColumns.at(-1)
      if (!lastColumn) return
      dropColumnId = lastColumn.databaseId
    } else {
      dropColumnId = dropMetadata.id
    }

    const repositionedColumnId = dragMetadata.id
    const previousColumnId: number | null = dropColumnId

    // removing reordered column
    const repositionedColumnIndex = allColumns.findIndex(({databaseId}) => databaseId === repositionedColumnId)
    const repositionedColumn = allColumns[repositionedColumnIndex]
    if (!repositionedColumn) return

    const notMovedColumns = [
      ...allColumns.slice(0, repositionedColumnIndex),
      ...allColumns.slice(repositionedColumnIndex + 1),
    ]

    // reinserting reordered column into its new position
    let previousColumnIndex = notMovedColumns.findIndex(({databaseId}) => databaseId === previousColumnId)
    previousColumnIndex = previousColumnIndex > -1 ? previousColumnIndex : notMovedColumns.length - 1

    const insertPosition = isBefore ? previousColumnIndex : previousColumnIndex + 1

    const reorderedColumns: Array<ColumnModel> = [
      ...notMovedColumns.slice(0, insertPosition),
      repositionedColumn,
      ...notMovedColumns.slice(insertPosition),
    ]

    // updating client-side position
    let position = 1
    for (const column of reorderedColumns) {
      ;(column as MemexColumn).position = position
      position += 1
    }

    setSortableColumns(getEditableColumns(reorderedColumns))

    // updating server position
    const dragColumn = sortableColumns.find(column => column.databaseId === dragMetadata.id)
    const dropColumn = sortableColumns.find(column => column.databaseId === dropMetadata?.id)
    if (!dragColumn || !dropColumn) {
      return
    }
    const allColumnsIndex = allColumns.indexOf(dropColumn)
    if (allColumnsIndex < 0) return
    const precedingColumnIndex = isBefore ? allColumnsIndex - 1 : allColumnsIndex
    const precedingColumn = allColumns[precedingColumnIndex]

    setReorderColumnCommitState('saving')
    try {
      await apiUpdateColumn({
        memexProjectColumnId: dragColumn.databaseId, // previous
        previousMemexProjectColumnId: precedingColumn?.databaseId, // next
      })
    } catch {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({message: "Reorder couldn't be saved", type: 'error'})
      setReorderColumnCommitState('error')
      return
    }

    setAllColumns(reorderedColumns)

    setReorderColumnCommitState('success')
    const message = saveFeedbackRef.current
    if (message?.textContent) {
      announce(message.textContent)
    }

    postStats({
      name: SettingsFieldReorder,
      ui: SettingsFieldReorderSettingsUI,
      memexProjectColumnId: dragColumn.databaseId,
      context: `previousColumnId: ${dropColumn.databaseId}`,
    })
  }

  return (
    <div className={styles.reorderCustomFieldsForm}>
      <div className={clsx('d-flex flex-items-center flex-justify-between', generalSettingsStyles.HeadingUnderline)}>
        <Heading
          as="h2"
          className={clsx(generalSettingsStyles.Heading)}
          {...testIdProps('reorder-custom-fields-heading')}
        >
          {issueFieldsEnabled ? 'Reorder fields' : 'Reorder custom fields'}
        </Heading>
        <CSSTransition
          nodeRef={saveFeedbackRef}
          in={reorderColumnCommitState === 'success'}
          timeout={reorderFeedbackTimeout}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('reorder-commit-state-feedback', styles.reorderFeedback)}
        >
          <BoxFeedbackCommitState ref={saveFeedbackRef} className={styles.reorderFeedback}>
            <CheckIcon /> Saved
          </BoxFeedbackCommitState>
        </CSSTransition>
      </div>
      <p>
        Change the order in which {issueFieldsEnabled ? 'fields' : 'custom fields'} appear on project items and in field
        lists.
      </p>
      <div>
        <div className={styles.listContainer}>
          {sortableColumns.length > 0 ? (
            <DragAndDrop
              items={sortableColumns.map(column => ({
                id: column.databaseId,
                title: column.name,
                data: column,
              }))}
              onDrop={onDrop}
              aria-label={issueFieldsEnabled ? 'Reorder fields list' : 'Reorder custom fields list'}
              renderOverlay={(item, index) => (
                <CustomFieldDraggableItem column={item.data} index={index} isDragOverlay routeParams={routeParams} />
              )}
            >
              {sortableColumns.map((column, index) => (
                <CustomFieldDraggableItem
                  key={column.databaseId}
                  column={column}
                  index={index}
                  isDragOverlay={false}
                  routeParams={routeParams}
                />
              ))}
            </DragAndDrop>
          ) : (
            <div className={styles.emptyContent}>
              {issueFieldsEnabled
                ? 'No fields are available to reorder.'
                : 'No sortable columns available. Only custom fields can be reordered.'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type CustomFieldDraggableItemProps = {
  column: ColumnModel
  index: number
  isDragOverlay: boolean
  routeParams: ReturnType<typeof useProjectRouteParams>
}

const containerStyle = {
  listStyleType: 'none',
} as React.CSSProperties

function CustomFieldDraggableItem({column, index, isDragOverlay, routeParams}: CustomFieldDraggableItemProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  const ColumnTypeIcon = getColumnIcon(column.dataType)
  const url = PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
    ...routeParams,
    fieldId: encodeURIComponent(column.id),
  })

  return (
    <DragAndDrop.Item
      isDragOverlay={isDragOverlay}
      index={index}
      id={column.databaseId}
      title={column.name}
      containerStyle={containerStyle}
      itemClassName={styles.item}
    >
      {/* eslint-disable-next-line react-hooks/static-components */}
      <ColumnTypeIcon />
      <SanitizedHtml as="p" className={styles.SanitizedHtml}>
        {column.name}
      </SanitizedHtml>
      <ActionMenu onOpenChange={setMenuOpen} open={menuOpen} anchorRef={editButtonRef}>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label={`Open field actions for ${column.name}`}
            size="small"
            ref={editButtonRef}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.LinkItem href={url}>
              <ActionList.LeadingVisual>
                <PencilIcon />
              </ActionList.LeadingVisual>
              Edit field
            </ActionList.LinkItem>
            <MoveDialogTrigger
              Component={ActionList.Item}
              aria-label={`Move field ${column.name}`}
              returnFocusRef={editButtonRef}
            >
              {/* False positive I am using an ActionList.Item */}
              {/* eslint-disable-next-line primer-react/direct-slot-children */}
              <ActionList.LeadingVisual>
                <GrabberIcon />
              </ActionList.LeadingVisual>
              Move field...
            </MoveDialogTrigger>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </DragAndDrop.Item>
  )
}
